// theme-system/SchemaContext.js
// React context for UI schema - the schema IS the template

import React from 'react';

/**
 * Schema context - provides the UI schema to all components
 */
export const SchemaContext = React.createContext({
  vendorId: null,
  hostname: '',
  schema: null,
  isLoading: false,
  error: null,
});

/**
 * Hook to access the full schema context
 * @returns {Object} { vendorId, hostname, schema, isLoading, error }
 */
export const useSchemaContext = () => {
  const context = React.useContext(SchemaContext);
  if (!context) {
    throw new Error('useSchemaContext must be used within SchemaContext.Provider');
  }
  return context;
};

/**
 * Hook to access just the schema
 * @returns {Object} The schema object
 */
export const useSchema = () => {
  const { schema } = useSchemaContext();
  return schema;
};

/**
 * Hook to access theme settings
 * @returns {Object} Theme settings object
 */
export const useThemeSettings = () => {
  const { schema } = useSchemaContext();
  return schema?.themeSettings || {};
};

/**
 * Hook to access page aliases
 * @returns {Object} Page aliases object
 */
export const useViews = () => {
  const { schema } = useSchemaContext();
  return schema?.views || {};
};

/**
 * Hook to access components for a specific page alias
 * @param {string} alias - The page alias (e.g., 'home', 'deposit')
 * @returns {Array} Array of component configs
 */
export const useView = (viewKey) => {
  const { schema } = useSchemaContext();
  const view = schema?.views?.[viewKey];
  if (viewKey === 'home' && Array.isArray(view?.section)) {
    return view.section;
  }
  return view?.components || [];
};
